import { EmojiValueKey, isEmoji } from "./emojis";
import { LinearTrackingEntity } from "./linearConnectionAPI";

export type ResolvedLinearIconKey = CustomIconKey | EmojiValueKey;

export function resolveLinearIconAndColor(
  trackingEntity?: LinearTrackingEntity | null,
) {
  const iconKey = trackingEntity?.icon;
  const resolvedIconKey = resolveLinearIcon(iconKey);
  const isResolvedEmoji = resolvedIconKey ? isEmoji(resolvedIconKey) : false;

  const iconColor =
    isResolvedEmoji || trackingEntity?.color === "#bec2c8"
      ? null
      : (trackingEntity?.color ?? null);

  return {
    icon: resolvedIconKey,
    iconColor,
  };
}

function resolveLinearIcon(
  iconKey?: string | null,
): ResolvedLinearIconKey | null {
  if (!iconKey) {
    return null;
  }

  if (isEmoji(iconKey)) {
    return iconKey;
  }

  const overrideMatch = LinearIconMap[iconKey];
  if (overrideMatch) {
    return overrideMatch;
  }

  return LinearIconEmojiFallbackMap[iconKey] ?? null;
}

// Custom icon keys available in the Reflag system
export type CustomIconKey =
  | "Default"
  | "Project"
  | "Issue"
  // AI & Machine Learning
  | "Ai"
  | "AiAgent"
  | "AiDocument"
  | "AiGenerate"
  | "AiWriting"
  | "CameraAi"
  | "CharacterRecognition"
  | "Robot"
  | "Bard"
  | "Claude"
  | "Copilot"
  | "Deepseek"
  | "Gemini"
  | "Mixtral"
  | "Openai"
  // Tech Brands & Platforms
  | "Android"
  | "Apple"
  | "GooglePlay"
  | "Chrome"
  | "Edge"
  | "Firefox"
  | "Finder"
  | "Mac"
  | "Css3"
  | "Html5"
  | "Javascript"
  | "Angularjs"
  | "Nextjs"
  | "Nodejs"
  | "Reactjs"
  | "Svelte"
  | "Vuejs"
  | "Firebase"
  | "Supabase"
  | "Discord"
  | "Figma"
  | "Github"
  | "Gitlab"
  | "Linear"
  | "Notion"
  | "Slack"
  // Programming & Development
  | "Bug"
  | "Cloud"
  | "CpuChip"
  | "Database"
  | "Merge"
  | "NodeTree"
  | "Server"
  | "Terminal"
  // Finance & Commerce
  | "Bank"
  | "CurrencyStack"
  | "DollarBill"
  | "DollarCircle"
  | "EuroCircle"
  | "Bitcoin"
  | "Ethereum"
  | "FundsChart"
  | "PriceTag"
  // Charts & Analytics
  | "BarChart"
  | "BubbleChart"
  | "Calendar"
  | "Dashboard"
  | "Dial"
  | "PieChart"
  // Communication
  | "ChatBubble"
  | "ChatCheck"
  | "Message"
  | "QuestionAnswer"
  | "Email"
  | "Megaphone"
  | "Microphone"
  | "Phone"
  | "Speaker"
  | "TextToSpeech"
  | "Video"
  // Nature & Weather
  | "Earth"
  | "Fire"
  | "Leaf"
  | "Moon"
  | "Sun"
  | "Thunderstorm"
  | "Tree"
  | "Umbrella"
  // Transportation
  | "Airplane"
  | "Sailboat"
  | "Bike"
  | "Car"
  | "Taxi"
  | "Truck"
  | "Bus"
  | "Subway"
  | "Train"
  // Sports & Games
  | "Basketball"
  | "PingPong"
  | "Runner"
  | "SoccerBall"
  | "ChessTower"
  | "Dice"
  | "Game"
  | "Gamepad"
  // Design & Creative
  | "Brush"
  | "DesignTools"
  | "Eraser"
  | "PaintBrush"
  | "PaintBucket"
  | "Pantone"
  | "Scissors"
  | "Signature"
  // Files & Documents
  | "Book"
  | "Bookmark"
  | "BookOpen"
  | "BookShelf"
  | "File"
  | "FileEdit"
  | "FloppyDisk"
  // Security & Access
  | "DoorLock"
  | "FaceId"
  | "Fingerprint"
  | "Key"
  | "Lock"
  | "StarShield"
  | "Verified"
  // UI & Interface Elements
  | "Accessibility"
  | "Alert"
  | "ArrowUpDouble"
  | "Asterisk"
  | "BellSubscribe"
  | "CheckCircle"
  | "Circle"
  | "Cross"
  | "Crosshair"
  | "Cursor"
  | "Edit"
  | "Grid"
  | "Notification"
  | "QuestionMark"
  | "Search"
  | "Settings"
  | "Toggle"
  | "Window"
  // E-commerce
  | "ShoppingBag"
  | "ShoppingCart"
  | "Store"
  // Location & Navigation
  | "Compass"
  | "Direction"
  | "Global"
  | "Globe"
  | "Landscape"
  | "Map"
  | "MapPin"
  | "Radar"
  | "Route"
  | "Router"
  | "Signpost"
  // Emotions & Reactions
  | "EmotionHappy"
  | "EmotionLaugh"
  | "EmotionNormal"
  | "EmotionUnhappy"
  | "EmotionVeryHappy"
  | "Heart"
  | "HeartPulse"
  | "ThumbDown"
  | "ThumbUp"
  // Food & Drinks
  | "Beer"
  | "Burger"
  | "Bowl"
  | "Coffee"
  | "Pizza"
  // Medical & Health
  | "Extinguisher"
  | "FirstAidKit"
  | "Pills"
  | "SurgicalMask"
  // Household & Objects
  | "Alarm"
  | "Anchor"
  | "Atom"
  | "Battery"
  | "Bed"
  | "Bluetooth"
  | "Briefcase"
  | "Calculator"
  | "Camera"
  | "Crown"
  | "Feather"
  | "Flag"
  | "Flashlight"
  | "Flask"
  | "Gift"
  | "Hourglass"
  | "Image"
  | "Ladder"
  | "LightBulb"
  | "MagicWand"
  | "Magnet"
  | "Medal"
  | "Plug"
  | "Printer"
  | "Radio"
  | "Recycle"
  | "Rocket"
  | "Skull"
  | "Star"
  | "Table"
  | "Tablet"
  | "Tape"
  | "Target"
  | "TShirt"
  | "VrGoggles"
  | "Wrench"
  // Buildings & Places
  | "Factory"
  | "GraduationCap"
  | "Home"
  // Actions & Symbols
  | "Exchange"
  | "Gavel"
  | "Trash"
  // Entertainment & Media
  | "MusicNote"
  | "VolumeMute"
  | "VolumeUp"
  // Technology & Hardware
  | "Computer"
  | "SignalTower"
  | "Smartphone"
  // Miscellaneous
  | "Anonymous"
  | "Brain"
  | "CustomerService"
  | "Dna"
  | "Footprint"
  | "Group"
  | "Stairs"
  | "Sword"
  | "TextBlock"
  | "TextSnippet"
  | "Time"
  | "TrafficCone"
  | "TrafficLight";

// Maps Linear icon names to explicit resolved icon keys.
// Entries can resolve to either Reflag custom icons or emoji values.
export const LinearIconMap: Record<string, ResolvedLinearIconKey | null> = {
  Accessibility: "Accessibility",
  Africa: "Earth", // Approximate match
  Ai: "Ai",
  AiApp: "AiGenerate",
  AiDocument: "AiDocument",
  Airplane: "Airplane",
  AirTag: null,
  AiWriting: "AiWriting",
  Alarm: "Alarm",
  AlarmClock: "Alarm",
  AlarmDelete: null,
  Alert: "Alert",
  AmericanFootBall: ":football:",
  Anchor: "Anchor",
  Android: "Android",
  Anonymous: "Anonymous",
  Apple: "Apple",
  Asia: "Earth", // Approximate match
  Asterisk: "Asterisk",
  Astronaut: null,
  Australia: "Earth", // Approximate match
  Auth0: "StarShield", // Approximate match
  Bandage: null,
  Bank: "Bank",
  BarChart: "BarChart",
  BarGraph: "BarChart",
  Basket: "ShoppingBag",
  Basketball: "Basketball",
  Battery: "Battery",
  Bed: "Bed",
  Bike: "Bike",
  Binocular: null,
  Bitcoin: "Bitcoin",
  Bluetooth: "Bluetooth",
  Bolt: "Flashlight",
  Bones: null,
  Book: "Book",
  Bookmark: "Bookmark",
  BookOpen: "BookOpen",
  Boombox: "Radio",
  BowlingBall: "SoccerBall",
  Box: "Project",
  BoxOpen: "Project",
  Brain: "Brain", // Approximate match
  Brick: null,
  Briefcase: "Briefcase",
  Brush: "Brush",
  Bucket: "PaintBucket", // Approximate match
  Bug: "Bug",
  Burger: "Burger",
  Bus: "Bus",
  Calculator: "Calculator",
  Calendar: "Calendar",
  CalendarRange: "Calendar",
  Camera: "Camera",
  CameraAi: "CameraAi",
  Car: "Car",
  CarPool: "Car",
  Cart: "ShoppingCart",
  Chart: "FundsChart",
  Chat: "ChatBubble",
  ChatLine: "Message", // Approximate match
  Chemist: "Flask",
  Chip: "CpuChip",
  Chrome: "Chrome",
  Circle: "Circle",
  ClickButton: "Cursor",
  Clock: "Time",
  ClockOutline: "Time",
  ClothHanger: null,
  Cloud: "Cloud",
  CodeBlock: "TextBlock",
  Coffee: "Coffee",
  Comodore: "Computer",
  Compass: "Compass",
  Computer: "Computer",
  Cone: "TrafficCone",
  Connected: "CpuChip", // Approximate match
  ConstructionBarrier: null,
  Conversation: "QuestionAnswer",
  Cookie: null,
  Crane: null,
  Cross: "Cross",
  Crown: "Crown",
  CrystalBall: null,
  Cube: "Project",
  Dashboard: "Dashboard",
  Database: "Database",
  DesignTools: "DesignTools",
  DesktopWindow: "Window",
  Dice: "Dice",
  Dino: null,
  Direction: "Direction",
  Discord: "Discord",
  Dna: "Dna",
  Dollar: "DollarCircle",
  DollarBill: "DollarBill",
  Dumbbell: ":weight_lifter:",
  Edge: "Edge",
  Education: "GraduationCap",
  ElectricPlug: "Plug",
  Email: "Email",
  Eraser: "Eraser",
  Etherum: "Ethereum",
  Euro: "EuroCircle",
  Europe: "Earth", // Approximate match
  ExclamationMark: "Alert", // Approximate match
  Extinguisher: "Extinguisher",
  Favorite: "Star",
  Face: "EmotionHappy",
  FaceFlatSmile: "EmotionNormal",
  FaceHeartEyes: "Heart",
  FaceId: "FaceId", // Approximate match
  FaceMask: "SurgicalMask", // Approximate match
  FaceMonocle: "FaceId",
  FaceStarEyes: "Star",
  FaceSunglasses: "FaceId",
  FaceSurprise: "EmotionVeryHappy", // Approximate match
  FaceTired: "FaceId",
  FaceTongue: "EmotionHappy",
  Factory: "Factory", // Approximate match
  Feather: "Feather", // Approximate match
  Figma: "Figma",
  Fire: "Fire",
  Firefox: "Firefox",
  Flashlight: "Flashlight",
  FloppyDisk: "FloppyDisk",
  FootPrint: "Footprint",
  Garage: null,
  GitHub: "Github",
  Gears: "Settings",
  Golf: "Flag", // Approximate match
  GooglePlay: "GooglePlay",
  Hack: "Game",
  Health: "HeartPulse",
  Hear: null,
  Heart: "Heart",
  Home: "Home",
  Hourglass: "Hourglass",
  Image: "Image",
  Intercom: null,
  Jersey: "TShirt", // Approximate match
  Joystick: "Gamepad", // Approximate match
  Judge: "Gavel",
  Ladder: "Ladder",
  Leaf: "Leaf",
  LightBulb: "LightBulb",
  Linear: "Linear",
  LinearAi: null,
  Lock: "Lock",
  LockerRoom: "BookShelf", // Approximate match
  Mac: "Mac",
  MacOS: "Finder",
  MagicWand: "MagicWand", // Approximate match
  Magnet: "Magnet",
  Mask: "SurgicalMask",
  Megaphone: "Megaphone",
  Mic: "Microphone",
  MilitaryTruck: "Truck", // Approximate match
  MobilePhone: "Smartphone",
  Modem: "Router", // Approximate match
  MoneyStack: "DollarBill", // Approximate match
  Moon: "Moon",
  Mountain: "Landscape", // Approximate match
  MovingStaircase: "Stairs", // Approximate match
  MusicKey: "MusicNote",
  MusicTape: "Tape",
  Network: "SignalTower",
  NorthAmerica: "Earth", // Approximate match
  NotePad: "FileEdit", // Approximate match
  Notified: "Notification",
  Notion: "Notion",
  OnePassword: "DoorLock", // Approximate match
  Page: "File",
  Paint: "PaintBrush",
  PalmTree: "Tree",
  Phone: "Phone",
  PieChart: "PieChart",
  Pills: "Pills",
  Pin: "MapPin",
  PingPong: "PingPong",
  Pizza: "Pizza",
  Pointer: "Cursor",
  PokerCard: "Pantone", // Approximate match
  Policy: "Toggle", // Approximate match
  Present: "Gift",
  Process: "Route", // Approximate match
  Project: "Project",
  QuestionMark: "QuestionMark",
  Radar: "Radar",
  Ramen: "Bowl",
  Rank: "ArrowUpDouble", // Approximate match
  Recycle: "Recycle",
  Report: "Flag", // Approximate match
  Resolved: "CheckCircle",
  ResolvedChat: "ChatCheck",
  Robot: "Robot",
  Rocket: "Rocket",
  Routing: "NodeTree",
  Runner: "Runner",
  SafetyKit: "FirstAidKit",
  ScatterPlot: "BubbleChart", // Approximate match
  Scissors: "Scissors",
  Search: "Search",
  Sentry: null,
  Server: "Server",
  Shield: "StarShield",
  Ship: "Sailboat",
  Shop: "Store",
  Shrug: null,
  Sign: "Signpost",
  Signature: "Signature",
  Skull: "Skull",
  Slack: "Slack",
  SoccerBall: "SoccerBall",
  Solana: null,
  Sound: "VolumeUp",
  SoundMuted: "VolumeMute",
  SouthAmerica: "Earth", // Approximate match
  Speedometer: "Dial",
  Spaces: null,
  Speaker: "Speaker",
  Spreadsheet: "Table",
  Stadium: null,
  Starred: "Star",
  Storm: "Thunderstorm",
  Subgroup: "Merge",
  Subscribe: "BellSubscribe",
  Sun: "Sun",
  Surfer: null,
  Tablet: "Tablet",
  Taxi: "Taxi",
  TeeShirt: "TShirt",
  TennisBall: "SoccerBall",
  Terminal: "Terminal",
  TextBlock: "CharacterRecognition",
  TextParagraph: "TextBlock",
  ThumbsDown: "ThumbDown",
  ThumbsUp: "ThumbUp",
  Tower: "ChessTower", // Approximate match
  TrafficLight: "TrafficLight",
  Train: "Train", // Approximate match
  Tram: "Subway",
  Trash: "Trash",
  Tree: "Tree",
  Umbrella: "Umbrella",
  UnhappyFace: "EmotionUnhappy",
  Union: "Exchange", // Approximate match
  Users: "Group",
  Video: "Video",
  ViewFinder: "Crosshair",
  VisionPro: "VrGoggles", // Approximate match
  Wall: "Grid",
  Watch: null,
  WindTurbine: null,
  World: "Global",
  Wrench: "Wrench",
  Write: "Edit",
  WritingAI: "AiWriting",
  Zapier: null,
  Zendesk: null,
};

const LinearIconEmojiFallbackMap: Partial<Record<string, EmojiValueKey>> = {
  Astronaut: ":astronaut:",
  Bandage: ":adhesive_bandage:",
  Binocular: ":telescope:",
  Bones: ":bone:",
  Brick: ":bricks:",
  ConstructionBarrier: ":construction:",
  Cookie: ":cookie:",
  Crane: ":building_construction:",
  Dino: ":t-rex:",
  Flower: ":hibiscus:",
  Garage: ":parking:",
  Hear: ":ear_with_hearing_aid:",
  Shrug: ":shrug:",
  Stadium: ":stadium:",
  Surfer: ":surfer:",
  Watch: ":watch:",
  WindTurbine: ":wind_blowing_face:",
};
